import React from 'react';
import styled from 'styled-components';
import { FormattedReactMessage } from '@99designs/frontend-components/i18n';
import { publicImageUrl } from '../../../lib/publicImageUrl';
import { media } from '../../../../shared/lib/styles';
import Attribution from '../../../../shared/components/Attribution';
import Columns, { Column } from '../../../../shared/components/Columns';
import Heading from '../../../../shared/components/Heading';
import Image from '../../../../shared/components/Image';
import ImageBorderedBox from '../../../../shared/components/ImageBorderedBox';
import { ChameleonLink } from '../../../../shared/components/Link';
import Scrollable from '../../../../shared/components/Scrollable';
import HowItWorksCard from '../../HowItWorks/HowItWorksCard';

const accentColour = '#39478C';

const StyledColumns = styled(Columns)`
    width: 100%;
    max-width: 1270px;
    min-width: 1014px;
    flex-shrink: 1;
    color: ${accentColour};

    @media ${media('medium')} {
        min-width: 870px;
    }

    @media ${media('small')} {
        min-width: 840px;
    }
`;

interface Props {
    languageCode: string;
}

function HowItWorksSection({ languageCode }: Props) {
    let description = (
        <FormattedReactMessage
            message={__(
                'Start by setting up your website through a web builder that best suits your goal. Our <Link>comparison chart</Link> below should help you make the right choice.'
            )}
            replacements={{
                Link: <ChameleonLink href="#compare" />,
            }}
        />
    );

    if (languageCode === 'fr') {
        description = (
            <FormattedReactMessage
                message={
                    'Mettez en place votre site web à l’aide du créateur de site web qui correspond le mieux à vos objectifs. Pour vous aider dans votre choix, vous pouvez consulter notre <Link>tableau comparatif</Link> et répondre à notre <OnboarderLink>questionnaire en ligne</OnboarderLink>.'
                }
                replacements={{
                    Link: <ChameleonLink href="#compare" />,
                    OnboarderLink: <ChameleonLink href="/web/get-started" />,
                }}
            />
        );
    }

    return (
        <>
            <div className="row">
                <div className="column">
                    <Heading
                        style={{ color: accentColour }}
                        type="h2"
                        variations={['h2', 'with-divider']}
                    >
                        {__('How it works')}
                    </Heading>
                </div>
            </div>

            <Scrollable variations={['full-page-width']}>
                <StyledColumns>
                    <Column>
                        <HowItWorksCard
                            stepNumber={__('01')}
                            title={__('Choose a web builder')}
                            description={description}
                            image={<StepOneImage />}
                        />
                    </Column>
                    <Column>
                        <HowItWorksCard
                            stepNumber={__('02')}
                            title={__('Get matched with expert designers')}
                            description={__(
                                "Complete a short brief and we'll match you with an expert designer of your choice. They will guide you through your website build and customize your design."
                            )}
                            image={<StepTwoImage />}
                        />
                    </Column>
                    <Column>
                        <HowItWorksCard
                            stepNumber={__('03')}
                            title={__('Bring your website to life')}
                            description={__(
                                "Your chosen designer will work closely with you to create a stunning and unique website design. Provide feedback throughout the process and only release payment when you're happy."
                            )}
                            image={<StepThreeImage />}
                        />
                    </Column>
                    <Column>
                        <HowItWorksCard
                            stepNumber={__('04')}
                            title={__('Easily manage your website')}
                            description={__(
                                'Once your freshly-designed website is live, you can log in to your web builder service at any time and easily manage your website (no complex coding knowledge needed).'
                            )}
                            image={<StepFourImage />}
                        />
                    </Column>
                </StyledColumns>
            </Scrollable>
        </>
    );
}

function StepOneImage() {
    return (
        <ImageBorderedBox
            image={<Image src={publicImageUrl('website-project/hiw-01.png')} alt="image" />}
            attribution={<Attribution name="spoon lancer" />}
        />
    );
}

function StepTwoImage() {
    return (
        <ImageBorderedBox
            image={<Image src={publicImageUrl('website-project/hiw-02.png')} alt="image" />}
        />
    );
}

function StepThreeImage() {
    return (
        <ImageBorderedBox
            image={<Image src={publicImageUrl('website-project/hiw-03.png')} alt="image" />}
        />
    );
}

function StepFourImage() {
    return (
        <ImageBorderedBox
            image={<Image src={publicImageUrl('website-project/hiw-04.png')} alt="image" />}
        />
    );
}

export default HowItWorksSection;
